import React, { useEffect, useRef } from "react";
import {
  Area,
  Bath,
  Bed,
  NewSparkle,
  Pricetag,
  Promote,
  VerifiedBadge,
} from "@/components/bnp-icons";
import { formatPrice } from "@/utils/helpers/main";
import { formatNumber } from "@/utils/formatNumber";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter";
import moment from "moment";
import { safeMoment } from "@/utils/safeMoment";
import { getPropertyType } from "@/components/pages/user/dashboard/properties/Rent";
import MediaPreview from "@/components/bnp-ui/image";
import { PropertyService } from "@/services";
import { realtorNameOutput } from "@/utils/helpers/outputUserName";
import { useCurrency } from "@/hooks/useCurrency";

const BuyDetailCard: any = ({
  id,
  address,
  realtor,
  description,
  status,
  listing_type,
  price,
  property_type,
  city,
  state,
  furnished,
  bedroom_count,
  bathroom_count,
  agency_fees,
  legal_fees,
  caution_fee,
  total_package,
  property_stage,
  additional_info,
  promote_to_weekly_deals,
  image_url,
  is_negotiable,
  created_at,
  currency,
  pricing_type,
  serviced,
  shared,
  currentSlide = 0,
  totalSlides = 1,
}: any) => {
  const isNewListing = moment().diff(safeMoment(created_at), "days") < 7;
  const isSponsored =
    realtor?.plan === "PLATINUM" || realtor?.plan === "DIAMOND";

  const isPartnerRealtor = realtor?.is_partner === true;

  const cardRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];

          if (entry.isIntersecting) {
            // Fire your event tracker
            PropertyService.TrackPropertyInsight(id, "impression");
            observer.disconnect(); // Clean up so it only fires once
          }
        },
        {
          threshold: 0.5, // 50% visible before firing
        },
      );

      observer.observe(node);
    },
    [id],
  );

  return (
    <div
      ref={cardRef}
      className="relative font-sans text-base w-full w-full sm:w-[350px] min-w-[300px] max-w-[350px] min-h-[561px] bg-white rounded-lg shadow-lg overflow-hidden"
    >
      <div className="absolute z-10 flex justify-between p-4 w-full sm:w-[350px]">
        <div className="flex justify-center font-bold items-center gap-2 bg-base-white text-bnp-dark p-2 sm:p-2 lg:p-0 text-sm min-w-[100px] min-h-[44px] sm:min-h-[28px] lg:w-[100px] lg:h-[28px] rounded-lg touch-manipulation active:scale-95 transition-transform">
          <Pricetag />
          <span className="font-bold">Sale</span>
        </div>
        <div className="flex flex-col gap-2">
          {isSponsored && (
            <div className="flex justify-center items-center gap-2 bg-purple-600 p-2 lg:p-0 text-sm text-white rounded-lg min-w-[123px] min-h-[44px] sm:min-h-[28px] lg:w-[123px] lg:h-[28px] touch-manipulation">
              <span className="font-bold">
                {realtor?.plan === "PLATINUM"
                  ? "Platinum Listing"
                  : realtor?.plan === "DIAMOND"
                    ? "Diamond Listing"
                    : ""}
              </span>
            </div>
          )}
          {promote_to_weekly_deals ? (
            <div className="flex justify-center items-center gap-2 bg-red-base p-2 lg:p-0 text-sm text-white rounded-lg min-w-[123px] min-h-[44px] sm:min-h-[28px] lg:w-[123px] lg:h-[28px] touch-manipulation active:scale-95 transition-transform">
              <Promote />
              <span className="font-bold">Deals</span>
            </div>
          ) : (
            isNewListing && (
              <div className="flex justify-center items-center gap-2 bg-bnp-dark p-2 lg:p-0 text-sm text-white rounded-lg min-w-[123px] min-h-[44px] sm:min-h-[28px] lg:w-[123px] lg:h-[28px] touch-manipulation">
                <NewSparkle />
                <span className="font-bold">New Listing</span>
              </div>
            )
          )}
        </div>
      </div>

      <MediaPreview
        src={image_url}
        alt={description}
        className="w-full h-[200px] sm:h-[220px] object-cover"
      />

      <div className="relative -mt-5 w-[95%] ml-auto mr-auto">
        <div className="bg-white rounded-t-xl">
          <div className="py-2 px-3">
            <div className="w-full flex items-center text-sm font-bold gap-2 sm:gap-4 text-soft-400 mb-4 border-b border-soft-200 pb-2">
              <div className="flex items-center gap-2 min-h-[44px] sm:min-h-0">
                <Bed />
                <span>{bedroom_count}</span>
              </div>
              <div className="border-r border-grey-5 h-4"></div>
              <div className="flex items-center gap-2 min-h-[44px] sm:min-h-0">
                <Bath />
                <span>{bathroom_count}</span>
              </div>
              {serviced && (
                <div className="flex gap-2 min-h-[44px] sm:min-h-0 items-center">
                  {" "}
                  <div className="border-r border-grey-5 h-4"></div>
                  <p className="text-sm">Serviced</p>
                </div>
              )}
              {shared && (
                <div className="flex gap-2 min-h-[44px] sm:min-h-0 items-center">
                  {" "}
                  <div className="border-r border-grey-5 h-4"></div>
                  <p className="text-sm">Shared</p>
                </div>
              )}
            </div>

            <h2 className="text-base sm:text-lg font-bold text-dark-blue mb-4 line-clamp-1">
              {capitalizeFirstLetter(description)}
            </h2>

            <div className="flex flex-row justify-between items-start gap-2 mb-2">
              <div className="flex flex-col gap-1">
                <div className="text-grey-4 text-sm">
                  Buy
                  {is_negotiable && (
                    <span className="ml-1 text-[10px] text-green-primary font-medium">
                      Negotiable
                    </span>
                  )}
                </div>

                {/* Price & Negotiable Badge Container */}
                <div className="flex flex-col items-start">
                  <span className="text-lg sm:text-xl font-bold text-bnp-cyan leading-snug">
                    {formatPrice(price, currency)}
                  </span>
                </div>
              </div>

              <span className="px-4 py-1 mt-1 bg-bnp-cyan/15 text-bnp-cyan text-xs rounded-lg font-bold flex items-center whitespace-nowrap">
                {safeMoment(created_at)?.fromNow() ?? "-"}
              </span>
            </div>

            <div className="space-y-1 sm:space-y-4 bg-gray-50 p-3 sm:p-4 rounded-lg text-sm min-h-[168px]">
              <div className="flex justify-between py-1 sm:py-0 items-center">
                <span className="text-grey-4 font-medium">Total Package</span>
                <span className="font-bold text-dark-blue">
                  {total_package
                    ? formatPrice(total_package, currency)
                    : "Price on request"}
                </span>
              </div>
              <div className="flex justify-between py-1 sm:py-0 items-center">
                <span className="text-grey-4 font-medium">Listed By</span>
                <div className="flex">
                  {" "}
                  {isPartnerRealtor && <VerifiedBadge />}
                  <span className="font-bold text-dark-blue  max-w-[150px] truncate">
                    {realtorNameOutput(
                      realtor?.business_name,
                      realtor?.first_name,
                      realtor?.last_name,
                    )}
                  </span>
                </div>
              </div>
              <div className="flex justify-between py-1 sm:py-0 items-center">
                <span className="text-grey-4 font-medium">Property Type</span>
                <span className="font-bold text-dark-blue">
                  {getPropertyType(property_type)}
                </span>
              </div>
              <div className="flex justify-between py-1 sm:py-0 items-center">
                <span className="text-grey-4 font-medium">Location</span>
                <span className="font-bold text-dark-blue max-w-[150px] truncate">
                  {address}
                </span>
              </div>
              <div className="flex justify-between py-1 sm:py-0 items-center">
                <span className="text-grey-4 font-medium">Furnished</span>
                <span className="font-bold text-dark-blue">
                  {furnished ? "Yes" : "No"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyDetailCard;
